import { Component, ReactNode } from 'react'

interface Props {
    children: ReactNode
    // When this changes, clear any latched error state. Used in place of a
    // `key=` on the boundary itself so a route change doesn't tear down the
    // entire children subtree — that previously killed in-flight flows like
    // GenieChat's "create chat then navigate" handshake.
    resetKey?: string
}

interface State {
    error: Error | null
}

class PageErrorBoundary extends Component<Props, State> {
    state: State = { error: null }

    static getDerivedStateFromError(error: Error): State {
        return { error }
    }

    componentDidCatch(error: Error, info: { componentStack?: string | null }) {
        console.error('PageErrorBoundary caught:', error, info.componentStack)
    }

    componentDidUpdate(prevProps: Props) {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null })
        }
    }

    render() {
        if (this.state.error) {
            return (
                <div className="text-center py-12 px-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        This page hit an error
                    </h2>
                    <p className="text-sm text-gray-600 mb-4 font-mono">
                        {this.state.error.message}
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-sky-600 text-white rounded hover:bg-sky-700"
                    >
                        Reload page
                    </button>
                </div>
            )
        }
        return this.props.children
    }
}

export default PageErrorBoundary
